import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { aiAPI, savedSearchesAPI, eventsAPI, favoritesAPI, eventScoutAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { EventCard } from '../components/EventCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Sparkles, Search, Loader2, MapPin, Compass, ArrowRight, CalendarIcon, Ruler, 
  Filter, Sun, Home as HomeIcon, Tag, X, Bookmark, BookmarkPlus, Clock, Trash2, Play,
  Link2, ExternalLink, CheckCircle2, AlertCircle, Check, Square, DollarSign, Navigation, Users,
  Star, TrendingUp, Award, Target, ThumbsUp, ThumbsDown, Settings
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { HelpButton } from '../components/HelpButton';

// Badge configurations for match scores
const MATCH_BADGES = {
  best_match: { label: 'Best Match', icon: Star, color: 'bg-yellow-500 text-white', priority: 1 },
  promising_sales: { label: 'High Sales Potential', icon: TrendingUp, color: 'bg-green-500 text-white', priority: 2 },
  large_audience: { label: 'Large Audience', icon: Users, color: 'bg-blue-500 text-white', priority: 3 },
  perfect_venue: { label: 'Perfect Venue', icon: Target, color: 'bg-purple-500 text-white', priority: 4 },
  ideal_location: { label: 'Ideal Location', icon: MapPin, color: 'bg-teal-500 text-white', priority: 5 },
  new_opportunity: { label: 'New Opportunity', icon: Sparkles, color: 'bg-orange-500 text-white', priority: 6 },
  avoid: { label: 'May Not Match', icon: AlertCircle, color: 'bg-red-100 text-red-600', priority: 99 }
};

const INDOOR_OUTDOOR = [
  { value: 'indoor', label: 'Indoor' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'both', label: 'Indoor & Outdoor' }
];

export const AIDiscover = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const resultsRef = useRef(null);
  
  // Enhanced filter states
  const [showFilters, setShowFilters] = useState(false);
  const [maxDistance, setMaxDistance] = useState([500]);
  const [keywords, setKeywords] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [indoorOutdoor, setIndoorOutdoor] = useState('');
  const [eventSize, setEventSize] = useState('');

  // Saved searches
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [savingSearch, setSavingSearch] = useState(false);

  // URL Import
  const [urlImportDialogOpen, setUrlImportDialogOpen] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importedEvent, setImportedEvent] = useState(null);
  const [importError, setImportError] = useState(null);

  // Selected events for batch add
  const [selectedEvents, setSelectedEvents] = useState(new Set());
  const [addingEvents, setAddingEvents] = useState(false);
  const [alsoShareToCommunity, setAlsoShareToCommunity] = useState(false);

  const hasActiveFilters = maxDistance[0] < 500 || keywords || dateFrom || dateTo || indoorOutdoor || eventSize;

  // Persist search results in sessionStorage
  useEffect(() => {
    const savedResults = sessionStorage.getItem('eventScoutResults');
    const savedQuery = sessionStorage.getItem('eventScoutQuery');
    const savedLocation = sessionStorage.getItem('eventScoutLocation');
    
    if (savedResults) {
      try {
        setResults(JSON.parse(savedResults));
      } catch (e) {
        console.error('Failed to restore search results');
      }
    }
    if (savedQuery) setQuery(savedQuery);
    if (savedLocation) setLocation(savedLocation);
  }, []);

  // Save results to sessionStorage when they change
  useEffect(() => {
    if (results) {
      sessionStorage.setItem('eventScoutResults', JSON.stringify(results));
      sessionStorage.setItem('eventScoutQuery', query);
      sessionStorage.setItem('eventScoutLocation', location);
    }
  }, [results, query, location]);

  // Load saved searches on mount
  useEffect(() => {
    if (user) {
      loadSavedSearches();
    }
  }, [user]);

  const loadSavedSearches = async () => {
    try {
      const res = await savedSearchesAPI.getAll();
      setSavedSearches(res.data);
    } catch (error) {
      console.error('Failed to load saved searches');
    }
  };

  const clearFilters = () => {
    setMaxDistance([500]);
    setKeywords('');
    setDateFrom(null);
    setDateTo(null);
    setIndoorOutdoor('');
    setEventSize('');
  };

  const getCurrentSearchParams = () => ({
    query,
    location: location || undefined,
    max_distance: maxDistance[0] < 500 ? maxDistance[0] : undefined,
    keywords: keywords || undefined,
    date_from: dateFrom ? format(dateFrom, 'yyyy-MM-dd') : undefined,
    date_to: dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined,
    indoor_outdoor: indoorOutdoor || undefined,
    event_size: eventSize || undefined
  });

  const applySearch = (search) => {
    setQuery(search.query || '');
    setLocation(search.location || '');
    setMaxDistance([search.max_distance || 500]);
    setKeywords(search.keywords || '');
    setDateFrom(search.date_from ? new Date(search.date_from) : null);
    setDateTo(search.date_to ? new Date(search.date_to) : null);
    setIndoorOutdoor(search.indoor_outdoor || '');
    setEventSize(search.event_size || '');
    
    // Show filters if any are set
    if (search.max_distance || search.keywords || search.date_from || search.date_to || 
        search.indoor_outdoor || search.event_size) {
      setShowFilters(true);
    }
    
    setShowSavedSearches(false);
    
    // Mark as used
    savedSearchesAPI.use(search.id).catch(() => {});
    
    toast.success(`Loaded search: ${search.name}`);
  };

  const handleSaveSearch = async () => {
    if (!searchName.trim()) {
      toast.error('Please enter a name for this search');
      return;
    }
    if (!query.trim()) {
      toast.error('Please enter a search query first');
      return;
    }

    setSavingSearch(true);
    try {
      const searchData = {
        name: searchName.trim(),
        ...getCurrentSearchParams()
      };
      
      await savedSearchesAPI.create(searchData);
      await loadSavedSearches();
      setSaveDialogOpen(false);
      setSearchName('');
      toast.success('Search saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save search');
    } finally {
      setSavingSearch(false);
    }
  };

  const handleDeleteSearch = async (searchId, searchName) => {
    try {
      await savedSearchesAPI.delete(searchId);
      setSavedSearches(prev => prev.filter(s => s.id !== searchId));
      toast.success(`Deleted "${searchName}"`);
    } catch (error) {
      toast.error('Failed to delete search');
    }
  };

  // URL Import handlers - now supports multiple events with duplicate detection
  const [importedEvents, setImportedEvents] = useState([]);
  const [importStats, setImportStats] = useState(null);
  const [previewData, setPreviewData] = useState(null);  // Preview data with duplicate info
  const [importDecisions, setImportDecisions] = useState([]);  // User decisions for each event
  const [confirmingImport, setConfirmingImport] = useState(false);
  const [saveToCommunity, setSaveToCommunity] = useState(false);  // Option to save to Community Events

  const handleUrlImport = async () => {
    if (!importUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(importUrl);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }

    setImportLoading(true);
    setImportedEvent(null);
    setImportedEvents([]);
    setImportStats(null);
    setImportError(null);
    setPreviewData(null);
    setImportDecisions([]);

    try {
      // Use the new preview endpoint for duplicate detection
      const res = await eventsAPI.previewImport(importUrl.trim());
      const data = res.data;
      
      if (data.extracted_events && data.extracted_events.length > 0) {
        setPreviewData(data);
        
        // Initialize decisions - default to 'create' for new, 'skip' for duplicates
        const decisions = data.extracted_events.map((item, index) => ({
          index,
          action: item.has_duplicates ? 'review' : 'create',  // 'review' means user needs to decide
          merge_target_id: item.duplicates?.[0]?.existing_event?.system_id || item.duplicates?.[0]?.existing_event?.id || null
        }));
        setImportDecisions(decisions);
        
        setImportStats({
          total: data.total_extracted,
          duplicates: data.total_with_duplicates,
          method: data.extraction_method
        });
        
        if (data.total_with_duplicates > 0) {
          toast.info(`Found ${data.total_with_duplicates} potential duplicate(s). Please review.`);
        } else {
          toast.success(`Extracted ${data.total_extracted} event(s). Ready to import!`);
        }
      } else {
        setImportError('No events found on this page');
        toast.error('No events found on this page');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to extract event data from URL';
      setImportError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setImportLoading(false);
    }
  };

  const updateDecision = (index, action, mergeTargetId = null) => {
    setImportDecisions(prev => prev.map((d, i) => 
      i === index ? { ...d, action, merge_target_id: mergeTargetId } : d
    ));
  };

  const handleConfirmImport = async () => {
    if (!previewData) return;
    
    setConfirmingImport(true);
    try {
      // Prepare events and decisions for confirmation
      const events = previewData.extracted_events.map(item => item.event);
      const decisions = importDecisions.map(d => ({
        action: d.action === 'review' ? 'skip' : d.action,  // Default 'review' to 'skip'
        merge_target_id: d.merge_target_id
      }));
      
      const res = await eventsAPI.confirmImport(events, decisions, saveToCommunity);
      const data = res.data;
      
      // Update state with saved events
      if (data.saved_events && data.saved_events.length > 0) {
        setImportedEvents(data.saved_events);
      }
      
      setImportStats(prev => ({
        ...prev,
        saved: data.total_saved,
        merged: data.total_merged,
        skipped: data.total_skipped
      }));
      
      toast.success(data.message);
      setPreviewData(null);  // Clear preview, show results
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save events');
    } finally {
      setConfirmingImport(false);
    }
  };

  const handleSaveImportedEvent = () => {
    if (importedEvent || importedEvents.length > 0) {
      setUrlImportDialogOpen(false);
      setImportUrl('');
      setImportedEvent(null);
      setImportedEvents([]);
      setImportStats(null);
      setImportError(null);
      setPreviewData(null);
      setImportDecisions([]);
      
      // Navigate based on what was imported
      if (importedEvents.length > 1) {
        toast.success(`${importedEvents.length} events saved to My Events!`);
        navigate('/events');
      } else if (importedEvents.length === 1) {
        toast.success('Event saved to your events!');
        navigate(`/events/${importedEvents[0].id}`);
      } else if (importedEvent) {
        toast.success('Event saved to your events!');
        navigate(`/events/${importedEvent.id}`);
      }
    }
  };

  const resetUrlImport = () => {
    setImportUrl('');
    setImportedEvent(null);
    setImportedEvents([]);
    setImportStats(null);
    setImportError(null);
    setPreviewData(null);
    setImportDecisions([]);
    setSaveToCommunity(false);
  };

  // Event selection handlers for batch add
  const toggleEventSelection = (eventId) => {
    setSelectedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const selectAllEvents = () => {
    if (results?.events) {
      setSelectedEvents(new Set(results.events.map(e => e.id)));
    }
  };

  const deselectAllEvents = () => {
    setSelectedEvents(new Set());
  };

  const handleAddSelectedEvents = async () => {
    if (selectedEvents.size === 0) {
      toast.error('Please select at least one event');
      return;
    }

    setAddingEvents(true);
    let addedCount = 0;
    let communityCount = 0;
    let errorCount = 0;

    try {
      // Get selected events from results
      const eventsToAdd = results.events.filter(e => selectedEvents.has(e.id));
      
      for (const event of eventsToAdd) {
        try {
          // First save the event to the database (remove preview flag)
          const eventData = {
            title: event.title,
            description: event.description || '',
            date: event.date,
            end_date: event.end_date || null,
            location: event.location || '',
            city: event.city || '',
            state: event.state || '',
            event_type: event.event_type || 'other',
            booth_cost: event.booth_cost || null,
            deadline: event.deadline || null,
            website_url: event.website_url || null,
            event_size: event.event_size || null,
            indoor_outdoor: event.indoor_outdoor || null,
            event_hours: event.event_hours || null,
            is_community: alsoShareToCommunity
          };
          
          // Create the event
          const createRes = await eventsAPI.create(eventData);
          
          // Then add it to favorites
          await favoritesAPI.add(createRes.data.id);
          addedCount++;
          
          if (alsoShareToCommunity) {
            communityCount++;
          }
        } catch (err) {
          console.error('Failed to add event:', event.title, err);
          errorCount++;
        }
      }

      if (addedCount > 0) {
        let message = `Added ${addedCount} event${addedCount > 1 ? 's' : ''} to My Events!`;
        if (communityCount > 0) {
          message += ` Also shared ${communityCount} to Community Events.`;
        }
        toast.success(message);
        setSelectedEvents(new Set());
      }
      if (errorCount > 0) {
        toast.error(`Failed to add ${errorCount} event${errorCount > 1 ? 's' : ''}`);
      }
    } catch (error) {
      toast.error('Failed to add events');
    } finally {
      setAddingEvents(false);
    }
  };

  const handleDiscover = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please log in to use Event Scout');
      navigate('/login');
      return;
    }

    if (!query.trim()) {
      toast.error('Please enter what kind of events you\'re looking for');
      return;
    }

    if (!location.trim()) {
      toast.error('Please enter a location to search near');
      return;
    }

    setLoading(true);
    setResults(null);
    setSelectedEvents(new Set()); // Clear selections on new search

    try {
      // Use the new hybrid Event Scout API
      const requestData = {
        query: query.trim(),
        location: location.trim(),
        max_distance: maxDistance[0] < 500 ? maxDistance[0] : undefined,
        date_from: dateFrom ? format(dateFrom, 'yyyy-MM-dd') : undefined,
        date_to: dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined,
        indoor_outdoor: indoorOutdoor || undefined,
        event_size: eventSize || undefined,
        min_confidence: 0  // Show all results, let user see confidence levels
      };
      
      const res = await eventScoutAPI.search(requestData);
      setResults(res.data);
      
      // Show appropriate message based on results
      if (res.data.events.length > 0) {
        const highConfidence = res.data.events.filter(e => e.confidence_score >= 80).length;
        const withWarnings = res.data.events.filter(e => e.validation_warnings?.length > 0).length;
        
        let message = `Found ${res.data.events.length} events`;
        if (highConfidence > 0) {
          message += ` (${highConfidence} high confidence)`;
        }
        if (withWarnings > 0) {
          message += `. ${withWarnings} need review.`;
        }
        toast.success(message, {
          action: {
            label: 'View on Map',
            onClick: () => {
              localStorage.setItem('eventsViewMode', 'map');
              navigate('/events');
            },
          },
        });
      } else {
        toast.info(res.data.message || 'No events found. Try a broader search.');
      }
      
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Event search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#1E3A5F] via-[#1E3A5F] to-[#0D1F42] py-16 px-6 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#1E3A5F] rounded-full blur-3xl opacity-10 -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FE5F55] rounded-full blur-3xl opacity-10 translate-y-1/2 -translate-x-1/2" />
        
        <div className="max-w-4xl mx-auto relative">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Sparkles className="w-8 h-8 text-[#1E3A5F]" />
              <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight text-white">
                Event Scout
              </h1>
              <HelpButton tutorialId="eventscout" className="text-white hover:bg-white/20" />
            </div>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-6">
              AI-powered search across Facebook Events, Eventbrite, Eventeny, ZAPP, and more
            </p>
            
            {/* Paste URL Button, Add Event Button, and What-If Planner */}
            {user && (
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <button
                  type="button"
                  onClick={() => setUrlImportDialogOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium bg-white text-emerald-700 hover:bg-emerald-50 transition-colors shadow-lg border border-emerald-200"
                  data-testid="paste-url-btn"
                >
                  <Link2 className="w-5 h-5" />
                  Paste Event URL
                </button>
                <Link to="/add-event">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium bg-white/20 text-white hover:bg-white/30 transition-colors"
                    data-testid="add-event-btn"
                  >
                    <CalendarIcon className="w-5 h-5" />
                    Add Event Manually
                  </button>
                </Link>
                <Link to="/what-if">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium bg-white/20 text-white hover:bg-white/30 transition-colors"
                    data-testid="what-if-planner-btn"
                  >
                    <Compass className="w-5 h-5" />
                    What-If Planner
                  </button>
                </Link>
              </div>
            )}
          </div>

          {/* Saved Searches Toggle */}
          {user && savedSearches.length > 0 && (
            <div className="mb-6">
              <button
                type="button"
                onClick={() => setShowSavedSearches(!showSavedSearches)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                  showSavedSearches
                    ? 'bg-[#1E3A5F] text-white'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                data-testid="toggle-saved-searches-btn"
              >
                <Bookmark className="w-4 h-4" />
                Saved Searches ({savedSearches.length})
              </button>

              {showSavedSearches && (
                <div className="mt-4 p-4 bg-white/95 backdrop-blur-sm rounded-2xl border-2 border-[#1E3A5F]">
                  <div className="grid gap-2">
                    {savedSearches.map((search) => (
                      <div
                        key={search.id}
                        className="flex items-center justify-between p-3 bg-[#FFEDDF] rounded-lg hover:bg-[#F5EDD0] transition-colors group"
                      >
                        <button
                          onClick={() => applySearch(search)}
                          className="flex-1 flex items-center gap-3 text-left"
                          data-testid={`saved-search-${search.id}`}
                        >
                          <Play className="w-4 h-4 text-[#1E3A5F]" />
                          <div>
                            <p className="font-medium text-[#1E3A5F]">{search.name}</p>
                            <p className="text-xs text-gray-500 truncate max-w-xs">
                              {search.query}
                              {search.location && ` • ${search.location}`}
                            </p>
                          </div>
                        </button>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {search.use_count}x
                          </span>
                          <button
                            onClick={() => handleDeleteSearch(search.id, search.name)}
                            className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`delete-search-${search.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleDiscover} className="space-y-4">
            {/* Main Search Input */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="What kind of events are you looking for? (e.g., craft shows, art fairs, farmers markets)"
                className="w-full pl-12 pr-4 py-6 text-lg bg-white border-2 border-[#1E3A5F] rounded-2xl shadow-lg focus:ring-4 focus:ring-[#1E3A5F]/30"
                data-testid="ai-query-input"
              />
            </div>

            {/* Location Input */}
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Location (city, state, or region)"
                className="w-full pl-12 pr-4 py-4 bg-white/90 border-2 border-white/50 rounded-xl focus:border-[#1E3A5F]"
                data-testid="ai-location-input"
              />
            </div>

            {/* Filters Toggle & Save Button */}
            <div className="flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                  showFilters || hasActiveFilters
                    ? 'bg-[#1E3A5F] text-white'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4" />
                Advanced Filters
                {hasActiveFilters && (
                  <span className="ml-1 w-5 h-5 rounded-full bg-[#FE5F55] text-white text-xs flex items-center justify-center font-bold">
                    {[maxDistance[0] < 500, keywords, dateFrom, dateTo, indoorOutdoor, eventSize].filter(Boolean).length}
                  </span>
                )}
              </button>

              {user && query.trim() && (
                <button
                  type="button"
                  onClick={() => setSaveDialogOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium bg-white/20 text-white hover:bg-white/30 transition-colors"
                  data-testid="save-search-btn"
                >
                  <BookmarkPlus className="w-4 h-4" />
                  Save Search
                </button>
              )}
            </div>

            {/* Enhanced Filter Panel */}
            {showFilters && (
              <div className="p-6 bg-white/95 backdrop-blur-sm rounded-2xl space-y-6 border-2 border-[#1E3A5F]">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Distance Filter */}
                  <div className="space-y-3">
                    <Label className="font-medium flex items-center gap-2 text-[#1E3A5F]">
                      <Ruler className="w-4 h-4" />
                      Max Distance
                    </Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        value={maxDistance[0] >= 500 ? '' : maxDistance[0]}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          setMaxDistance([Math.min(Math.max(val, 0), 500)]);
                        }}
                        placeholder="Any"
                        className="input-default w-24"
                        data-testid="filter-distance-input"
                      />
                      <span className="text-sm text-[#4A4A4A]">miles</span>
                    </div>
                    <Slider
                      value={maxDistance}
                      onValueChange={setMaxDistance}
                      max={500}
                      step={25}
                      className="mt-2"
                      data-testid="filter-distance-slider"
                    />
                    <p className="text-xs text-[#71717A]">
                      {maxDistance[0] >= 500 ? 'Showing all distances' : `Within ${maxDistance[0]} miles of home`}
                    </p>
                    {!user?.home_state && (
                      <p className="text-xs text-[#FE5F55]">
                        Set home location in <Link to="/settings" className="underline">Settings</Link> to use distance filter
                      </p>
                    )}
                  </div>

                  {/* Keywords Filter */}
                  <div className="space-y-2">
                    <Label className="font-medium flex items-center gap-2 text-[#1E3A5F]">
                      <Tag className="w-4 h-4" />
                      Keywords
                    </Label>
                    <Input
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                      placeholder="e.g., handmade, vintage, organic"
                      className="input-default"
                      data-testid="filter-keywords"
                    />
                  </div>

                  {/* Indoor/Outdoor Filter */}
                  <div className="space-y-2">
                    <Label className="font-medium flex items-center gap-2 text-[#1E3A5F]">
                      <Sun className="w-4 h-4" />
                      Venue Type
                    </Label>
                    <Select value={indoorOutdoor} onValueChange={setIndoorOutdoor}>
                      <SelectTrigger className="input-default" data-testid="filter-indoor-outdoor">
                        <SelectValue placeholder="Any venue" />
                      </SelectTrigger>
                      <SelectContent className="border-2 border-[#1E3A5F] rounded-xl bg-white">
                        <SelectItem value="any">Any venue</SelectItem>
                        {INDOOR_OUTDOOR.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date From Filter */}
                  <div className="space-y-2">
                    <Label className="font-medium flex items-center gap-2 text-[#1E3A5F]">
                      <CalendarIcon className="w-4 h-4" />
                      From Date
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "input-default w-full justify-start text-left font-normal",
                            !dateFrom && "text-gray-400"
                          )}
                          data-testid="filter-date-from"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateFrom ? format(dateFrom, 'PPP') : 'Any start date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                        <Calendar
                          mode="single"
                          selected={dateFrom}
                          onSelect={setDateFrom}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Date To Filter */}
                  <div className="space-y-2">
                    <Label className="font-medium flex items-center gap-2 text-[#1E3A5F]">
                      <CalendarIcon className="w-4 h-4" />
                      To Date
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "input-default w-full justify-start text-left font-normal",
                            !dateTo && "text-gray-400"
                          )}
                          data-testid="filter-date-to"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateTo ? format(dateTo, 'PPP') : 'Any end date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                        <Calendar
                          mode="single"
                          selected={dateTo}
                          onSelect={setDateTo}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Event Size Filter */}
                  <div className="space-y-2">
                    <Label className="font-medium flex items-center gap-2 text-[#1E3A5F]">
                      <HomeIcon className="w-4 h-4" />
                      Event Size
                    </Label>
                    <Select value={eventSize} onValueChange={setEventSize}>
                      <SelectTrigger className="input-default" data-testid="filter-event-size">
                        <SelectValue placeholder="Any size" />
                      </SelectTrigger>
                      <SelectContent className="border-2 border-[#1E3A5F] rounded-xl bg-white">
                        <SelectItem value="any">Any size</SelectItem>
                        <SelectItem value="small">Small (under 50 vendors)</SelectItem>
                        <SelectItem value="medium">Medium (50-150 vendors)</SelectItem>
                        <SelectItem value="large">Large (150+ vendors)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {hasActiveFilters && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="flex items-center gap-1 text-sm text-[#71717A] hover:text-[#FE5F55]"
                    >
                      <X className="w-4 h-4" />
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !user}
              className="btn-ai w-full text-lg py-6"
              data-testid="ai-discover-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Searching across platforms...
                </>
              ) : (
                <>
                  <Compass className="w-5 h-5 mr-2" />
                  Discover Events
                </>
              )}
            </Button>

            {!user && (
              <p className="text-center text-white/70">
                <Link to="/login" className="text-[#1E3A5F] hover:underline font-semibold">Log in</Link>
                {' '}to use Event Scout
              </p>
            )}
          </form>
        </div>
      </div>

      {/* Search Sources Info */}
      <div className="bg-[#FFEDDF] py-6 px-6 border-y-2 border-[#1E3A5F]">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-sm text-[#4A4A4A]">
            <span className="font-semibold text-[#1E3A5F]">Searching:</span> Facebook Events • Eventbrite • Eventeny.com • ZAPP • Google
          </p>
        </div>
      </div>

      {/* Results Section */}
      <div ref={resultsRef} className="py-12 px-6 md:px-12 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto">
          {/* Profile Setup Reminder */}
          {results && !user?.craft_description && !user?.show_preferences && (
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border-2 border-purple-200">
              <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-purple-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-purple-800 mb-1">Get Personalized Match Scores</h3>
                  <p className="text-sm text-purple-700">
                    Set up your artisan profile to see match scores and badges for each event. We'll analyze how well each event fits your craft and preferences.
                  </p>
                </div>
                <Link to="/settings">
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
                    <Settings className="w-4 h-4 mr-1" />
                    Setup Profile
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Results with match scores indicator */}
          {results && (user?.craft_description || user?.show_preferences) && (
            <div className="mb-6 p-3 bg-green-50 rounded-xl border border-green-200 flex items-center gap-2">
              <Star className="w-5 h-5 text-green-600" />
              <span className="text-sm text-green-700">
                <strong>Match Scoring Active:</strong> Events are ranked by compatibility with your artisan profile
              </span>
            </div>
          )}

          {results && (
            <>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                  <h2 className="text-2xl font-bold font-['Outfit'] text-[#1E3A5F]">
                    Discovered Events
                  </h2>
                  <p className="text-[#4A4A4A]">
                    Found {results.events.length} events matching your search
                    {selectedEvents.size > 0 && (
                      <span className="ml-2 text-[#1E3A5F] font-semibold">
                        • {selectedEvents.size} selected
                      </span>
                    )}
                  </p>
                  {/* Sources searched */}
                  {results.sources_searched && results.sources_searched.length > 0 && (
                    <p className="text-xs text-[#71717A] mt-1">
                      Sources: {results.sources_searched.join(', ')}
                    </p>
                  )}
                  {/* Confidence summary */}
                  {results.events.length > 0 && (
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle2 className="w-3 h-3" />
                        {results.events.filter(e => e.confidence_score >= 80).length} high confidence
                      </span>
                      {results.events.filter(e => e.validation_warnings?.length > 0).length > 0 && (
                        <span className="flex items-center gap-1 text-yellow-600">
                          <AlertCircle className="w-3 h-3" />
                          {results.events.filter(e => e.validation_warnings?.length > 0).length} need review
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Selection controls */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={selectAllEvents}
                      className="text-sm text-[#1E3A5F] hover:text-[#1E3A5F] font-medium"
                      data-testid="select-all-btn"
                    >
                      Select All
                    </button>
                    <span className="text-[#71717A]">|</span>
                    <button
                      type="button"
                      onClick={deselectAllEvents}
                      className="text-sm text-[#1E3A5F] hover:text-[#1E3A5F] font-medium"
                      data-testid="deselect-all-btn"
                    >
                      Deselect All
                    </button>
                  </div>
                  
                  {/* Add Selected button */}
                  <Button
                    onClick={handleAddSelectedEvents}
                    disabled={selectedEvents.size === 0 || addingEvents}
                    className="bg-[#1E3A5F] hover:bg-[#1E3A5F] text-white font-bold disabled:opacity-50"
                    data-testid="add-selected-btn"
                  >
                    {addingEvents ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Add Selected to My Events ({selectedEvents.size})
                      </>
                    )}
                  </Button>
                  
                  {/* Also share to Community checkbox */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border-2 border-[#1E3A5F]">
                    <Switch
                      id="share-to-community"
                      checked={alsoShareToCommunity}
                      onCheckedChange={setAlsoShareToCommunity}
                      data-testid="share-to-community-toggle"
                    />
                    <Label 
                      htmlFor="share-to-community" 
                      className="text-sm font-medium text-[#1E3A5F] cursor-pointer flex items-center gap-1.5"
                    >
                      <Users className="w-4 h-4" />
                      Also share to Community
                    </Label>
                  </div>
                  
                  <Link to="/events">
                    <Button variant="outline" className="border-2 border-[#1E3A5F] text-[#1E3A5F]">
                      View My Events
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Hint for first-time users */}
              {selectedEvents.size === 0 && (
                <div className="mb-6 p-4 bg-[#FFEDDF] border-2 border-[#1E3A5F] rounded-xl flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1E3A5F] flex-shrink-0" />
                  <p className="text-sm text-[#1E3A5F]">
                    <strong>Tip:</strong> Click the checkbox on any event card to select it, then click "Add Selected to My Events" to save them all at once.
                  </p>
                </div>
              )}

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {results.events.map((event) => (
                  <PreviewEventCard 
                    key={event.id} 
                    event={event} 
                    distance={event.distance_miles}
                    isSelected={selectedEvents.has(event.id)}
                    onToggleSelect={() => toggleEventSelection(event.id)}
                  />
                ))}
              </div>
            </>
          )}

          {!results && !loading && (
            <div className="text-center py-16">
              <Sparkles className="w-16 h-16 text-[#1E3A5F] mx-auto mb-4" />
              <h3 className="text-xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">
                Ready to Discover
              </h3>
              <p className="text-[#4A4A4A] max-w-md mx-auto">
                Enter your search criteria above to find vendor opportunities across multiple platforms
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Save Search Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="border-2 border-[#1E3A5F] rounded-xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1E3A5F] flex items-center gap-2">
              <BookmarkPlus className="w-5 h-5 text-[#1E3A5F]" />
              Save This Search
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-medium text-[#1E3A5F]">Search Name</Label>
              <Input
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="e.g., NC Craft Shows Spring 2026"
                className="input-default"
                data-testid="search-name-input"
              />
            </div>
            
            <div className="p-3 bg-[#FFEDDF] rounded-lg">
              <p className="text-sm text-[#4A4A4A]">
                <strong>Query:</strong> {query}
              </p>
              {location && (
                <p className="text-sm text-[#4A4A4A]">
                  <strong>Location:</strong> {location}
                </p>
              )}
              {hasActiveFilters && (
                <p className="text-sm text-[#4A4A4A]">
                  <strong>Filters:</strong> {[
                    maxDistance[0] < 500 && `${maxDistance[0]} miles`,
                    keywords && `keywords: ${keywords}`,
                    indoorOutdoor && indoorOutdoor,
                    eventSize && eventSize
                  ].filter(Boolean).join(', ')}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveDialogOpen(false)}
              className="border-2 border-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSearch}
              disabled={savingSearch || !searchName.trim()}
              className="btn-primary"
              data-testid="confirm-save-search-btn"
            >
              {savingSearch ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Bookmark className="w-4 h-4 mr-2" />
              )}
              Save Search
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URL Import Dialog with Duplicate Detection */}
      <Dialog open={urlImportDialogOpen} onOpenChange={(open) => {
        setUrlImportDialogOpen(open);
        if (!open) resetUrlImport();
      }}>
        <DialogContent className="border-2 border-[#1E3A5F] rounded-xl bg-white sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1E3A5F] flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#1E3A5F]" />
              Import Event from URL
            </DialogTitle>
            <DialogDescription>
              Paste a link to an event webpage. We'll detect duplicates and let you choose how to handle them.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Step 1: Enter URL */}
            {!previewData && !importedEvents.length && (
              <>
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Event URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={importUrl}
                      onChange={(e) => setImportUrl(e.target.value)}
                      placeholder="https://zapplication.org/event-info.php?ID=..."
                      className="input-default flex-1"
                      data-testid="import-url-input"
                      disabled={importLoading}
                    />
                    <Button
                      onClick={handleUrlImport}
                      disabled={importLoading || !importUrl.trim()}
                      className="btn-primary"
                      data-testid="extract-event-btn"
                    >
                      {importLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Preview'
                      )}
                    </Button>
                  </div>
                </div>

                {importLoading && (
                  <div className="p-4 bg-blue-50 rounded-lg flex items-center gap-3">
                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                    <div>
                      <p className="font-medium text-blue-800">Analyzing page & checking for duplicates...</p>
                      <p className="text-xs text-blue-600">This may take up to 30 seconds</p>
                    </div>
                  </div>
                )}

                <div className="p-3 bg-[#FFEDDF] rounded-lg">
                  <p className="text-xs text-[#4A4A4A] mb-2 font-medium">Smart Import Features:</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {['Duplicate Detection', 'Merge Options', 'Multi-event Pages', 'JS-rendered Sites'].map((item) => (
                      <span key={item} className="px-2 py-1 bg-white rounded text-xs text-[#1E3A5F]">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {importError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700">{importError}</p>
                  </div>
                )}
              </>
            )}

            {/* Step 2: Preview with Duplicate Detection */}
            {previewData && (
              <>
                <div className={`p-4 rounded-xl border-2 ${
                  importStats?.duplicates > 0 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {importStats?.duplicates > 0 ? (
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    )}
                    <div>
                      <p className={`font-semibold ${importStats?.duplicates > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                        Found {importStats?.total} event(s)
                        {importStats?.duplicates > 0 && ` • ${importStats.duplicates} potential duplicate(s)`}
                      </p>
                      <p className="text-xs text-gray-600">
                        Review each event below and choose how to handle duplicates
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                  {previewData.extracted_events.map((item, index) => (
                    <div 
                      key={index} 
                      className={`border-2 rounded-xl p-4 ${
                        item.has_duplicates 
                          ? 'border-amber-300 bg-amber-50/50' 
                          : 'border-[#E5E7EB]'
                      }`}
                    >
                      {/* Match Score Badge */}
                      {item.match_score !== undefined && (
                        <div className="flex items-center justify-between mb-3">
                          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                            item.match_score >= 70 
                              ? 'bg-green-100 text-green-700 border border-green-200'
                              : item.match_score >= 50 
                                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                                : 'bg-gray-100 text-gray-700 border border-gray-200'
                          }`}>
                            <Star className="w-4 h-4" />
                            {item.match_score}% Match
                          </div>
                          {item.distance_miles && (
                            <span className="text-sm text-[#71717A] flex items-center gap-1">
                              <Compass className="w-4 h-4" />
                              {Math.round(item.distance_miles)} miles away
                            </span>
                          )}
                        </div>
                      )}

                      {/* Event Info */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-[#1E3A5F] text-lg">{item.event.title}</h4>
                          
                          {/* Date Range */}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-[#4A4A4A]">
                            {item.event.date && (
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-4 h-4 text-[#FE5F55]" />
                                {format(new Date(item.event.date + 'T00:00:00'), 'MMM d, yyyy')}
                                {item.event.end_date && item.event.end_date !== item.event.date && (
                                  <> - {format(new Date(item.event.end_date + 'T00:00:00'), 'MMM d, yyyy')}</>
                                )}
                              </span>
                            )}
                            {item.event.booth_cost && (
                              <span className="flex items-center gap-1 font-medium text-[#1E3A5F]">
                                <DollarSign className="w-4 h-4 text-green-600" />
                                ${item.event.booth_cost}
                                {item.event.booth_cost_notes && (
                                  <span className="text-xs text-[#71717A] font-normal ml-1">({item.event.booth_cost_notes})</span>
                                )}
                              </span>
                            )}
                          </div>

                          {/* Location & Address */}
                          <div className="mt-2 space-y-1">
                            {item.event.venue_type && (
                              <p className="text-sm font-medium text-[#1E3A5F]">{item.event.venue_type}</p>
                            )}
                            {item.event.location && (
                              <p className="text-sm text-[#4A4A4A] flex items-start gap-1">
                                <MapPin className="w-4 h-4 text-[#FE5F55] flex-shrink-0 mt-0.5" />
                                {item.event.location}
                              </p>
                            )}
                            {(item.event.city || item.event.state || item.event.zip) && !item.event.location && (
                              <p className="text-sm text-[#4A4A4A] flex items-start gap-1">
                                <MapPin className="w-4 h-4 text-[#FE5F55] flex-shrink-0 mt-0.5" />
                                {[item.event.city, item.event.state, item.event.zip].filter(Boolean).join(', ')}
                              </p>
                            )}
                          </div>

                          {/* Description */}
                          {item.event.description && (
                            <p className="mt-2 text-sm text-[#4A4A4A] line-clamp-3 bg-gray-50 p-2 rounded-lg">
                              {item.event.description}
                            </p>
                          )}

                          {/* Additional Details Grid */}
                          <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-[#71717A]">
                            {item.event.event_hours && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {item.event.event_hours}
                              </span>
                            )}
                            {item.event.indoor_outdoor && (
                              <span className="capitalize flex items-center gap-1">
                                {item.event.indoor_outdoor === 'indoor' ? '🏠' : item.event.indoor_outdoor === 'outdoor' ? '🌳' : '🏠🌳'}
                                {item.event.indoor_outdoor}
                              </span>
                            )}
                            {item.event.event_type && (
                              <span className="capitalize flex items-center gap-1">
                                🎪 {item.event.event_type.replace(/_/g, ' ')}
                              </span>
                            )}
                            {item.event.expected_attendance && (
                              <span className="flex items-center gap-1">
                                👥 {item.event.expected_attendance} expected
                              </span>
                            )}
                            {item.event.deadline && (
                              <span className="flex items-center gap-1 text-amber-600 font-medium">
                                ⏰ Deadline: {format(new Date(item.event.deadline + 'T00:00:00'), 'MMM d, yyyy')}
                              </span>
                            )}
                          </div>

                          {/* Match Reasons */}
                          {item.match_reasons && item.match_reasons.length > 0 && (
                            <div className="mt-3 p-2 bg-green-50 rounded-lg border border-green-100">
                              <p className="text-xs font-medium text-green-800 mb-1">Why this matches you:</p>
                              <ul className="text-xs text-green-700 space-y-0.5">
                                {item.match_reasons.map((reason, idx) => (
                                  <li key={idx} className="flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" /> {reason}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Links */}
                          <div className="mt-3 flex flex-wrap gap-2">
                            {item.event.website_url && (
                              <a 
                                href={item.event.website_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-[#1E3A5F] hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" /> Website
                              </a>
                            )}
                            {item.event.signup_url && (
                              <a 
                                href={item.event.signup_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-green-600 hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" /> Apply Now
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Duplicate Warning & Actions */}
                      {item.has_duplicates && (
                        <div className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                          <p className="text-sm font-medium text-amber-800 mb-2">
                            ⚠️ Potential duplicate found:
                          </p>
                          <div className="text-xs text-gray-600 mb-3 p-2 bg-gray-50 rounded">
                            <p><strong>Existing:</strong> {item.duplicates[0]?.existing_event?.title}</p>
                            <p><strong>Match:</strong> {item.duplicates[0]?.matching_fields?.join(', ')}</p>
                            <p><strong>Confidence:</strong> {Math.round((item.duplicates[0]?.match_confidence || 0) * 100)}%</p>
                          </div>
                          
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant={importDecisions[index]?.action === 'create' ? 'default' : 'outline'}
                              onClick={() => updateDecision(index, 'create')}
                              className={importDecisions[index]?.action === 'create' ? 'bg-green-600 hover:bg-green-700' : ''}
                            >
                              Create Anyway
                            </Button>
                            <Button
                              size="sm"
                              variant={importDecisions[index]?.action === 'merge' ? 'default' : 'outline'}
                              onClick={() => updateDecision(index, 'merge', item.duplicates[0]?.existing_event?.system_id || item.duplicates[0]?.existing_event?.id)}
                              className={importDecisions[index]?.action === 'merge' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                            >
                              Merge Into Existing
                            </Button>
                            <Button
                              size="sm"
                              variant={importDecisions[index]?.action === 'skip' ? 'default' : 'outline'}
                              onClick={() => updateDecision(index, 'skip')}
                              className={importDecisions[index]?.action === 'skip' ? 'bg-gray-600 hover:bg-gray-700' : ''}
                            >
                              Skip
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Non-duplicate: Show create/skip */}
                      {!item.has_duplicates && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded font-medium">
                            ✓ No duplicates
                          </span>
                          <span className="text-xs text-gray-500">
                            Will be imported as new event
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Summary */}
                <div className="p-3 bg-gray-50 rounded-lg text-sm">
                  <p className="font-medium text-gray-700">Import Summary:</p>
                  <div className="flex gap-4 mt-1 text-gray-600">
                    <span>✓ Create: {importDecisions.filter(d => d.action === 'create').length}</span>
                    <span>↗ Merge: {importDecisions.filter(d => d.action === 'merge').length}</span>
                    <span>✕ Skip: {importDecisions.filter(d => d.action === 'skip' || d.action === 'review').length}</span>
                  </div>
                </div>

                {/* Save to Community Option */}
                <div className="flex items-center justify-between p-4 bg-[#FFEDDF]/50 rounded-xl border border-[#1E3A5F]">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-[#1E3A5F] mt-0.5" />
                    <div>
                      <Label htmlFor="save-to-community" className="font-medium text-[#1E3A5F] cursor-pointer">
                        Save to Community Events
                      </Label>
                      <p className="text-sm text-[#4A4A4A] mt-1">
                        Make these events visible to all AMSCompass members
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="save-to-community"
                    checked={saveToCommunity}
                    onCheckedChange={setSaveToCommunity}
                    data-testid="save-to-community-switch"
                  />
                </div>
              </>
            )}

            {/* Step 3: Import Complete */}
            {importedEvents.length > 0 && !previewData && (
              <>
                <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-800">
                        Import Complete!
                      </p>
                      <p className="text-xs text-green-600">
                        {importStats?.saved || 0} saved, {importStats?.merged || 0} merged, {importStats?.skipped || 0} skipped
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                  {importedEvents.map((event, index) => (
                    <div key={event.id || index} className="border border-green-200 rounded-lg p-3 bg-green-50/50">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-[#1E3A5F] truncate">{event.title}</h4>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-[#4A4A4A]">
                            {event.date && (
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-3 h-3 text-[#FE5F55]" />
                                {format(new Date(event.date + 'T00:00:00'), 'MMM d, yyyy')}
                                {event.end_date && event.end_date !== event.date && (
                                  <> - {format(new Date(event.end_date + 'T00:00:00'), 'MMM d, yyyy')}</>
                                )}
                              </span>
                            )}
                            {(event.city || event.state) && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-[#FE5F55]" />
                                {[event.city, event.state].filter(Boolean).join(', ')}
                              </span>
                            )}
                          </div>
                          {/* Additional saved event details */}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-[#71717A]">
                            {event.event_hours && (
                              <span>Hours: {event.event_hours}</span>
                            )}
                            {event.indoor_outdoor && (
                              <span className="capitalize">{event.indoor_outdoor}</span>
                            )}
                            {event.booth_cost && (
                              <span>Booth: ${event.booth_cost}</span>
                            )}
                          </div>
                        </div>
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded font-medium">
                          Saved
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            {!previewData && !importedEvents.length ? (
              <Button
                variant="outline"
                onClick={() => setUrlImportDialogOpen(false)}
                className="border-2 border-gray-300"
              >
                Cancel
              </Button>
            ) : previewData ? (
              <>
                <Button
                  variant="outline"
                  onClick={resetUrlImport}
                  className="border-2 border-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  disabled={confirmingImport || importDecisions.filter(d => d.action === 'create' || d.action === 'merge').length === 0}
                  className="btn-primary"
                  data-testid="confirm-import-btn"
                >
                  {confirmingImport ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Import {importDecisions.filter(d => d.action === 'create' || d.action === 'merge').length} Event(s)
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={resetUrlImport}
                  className="border-2 border-gray-300"
                >
                  Import Another
                </Button>
                <Button
                  onClick={handleSaveImportedEvent}
                  className="btn-primary"
                  data-testid="view-events-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  View Events
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// PreviewEventCard - Special card for AI-discovered events with checkbox selection
const PreviewEventCard = ({ event, distance, isSelected, onToggleSelect }) => {
  // Format date display
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Get event type icon and label
  const getEventTypeInfo = (type) => {
    const types = {
      'craft_show': { icon: '🎨', label: 'CRAFT SHOW', bg: 'bg-white' },
      'art_fair': { icon: '🖼️', label: 'ART FAIR', bg: 'bg-[#1E3A5F] text-white' },
      'farmers_market': { icon: '🥕', label: 'FARMERS MARKET', bg: 'bg-green-600 text-white' },
      'festival': { icon: '🎪', label: 'FESTIVAL', bg: 'bg-[#FE5F55] text-white' },
      'holiday_market': { icon: '🎄', label: 'HOLIDAY MARKET', bg: 'bg-red-600 text-white' },
      'art_festival': { icon: '🎭', label: 'ART FESTIVAL', bg: 'bg-[#1E3A5F] text-white' },
      'street_fair': { icon: '🎠', label: 'STREET FAIR', bg: 'bg-orange-500 text-white' },
      'home_show': { icon: '🏠', label: 'HOME SHOW', bg: 'bg-amber-600 text-white' },
      'other': { icon: '📅', label: 'OTHER', bg: 'bg-white' }
    };
    return types[type] || types['other'];
  };

  // Get confidence score color - now using confidence_score from Event Scout
  const getConfidenceColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 60) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 40) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  // Get trust level badge color
  const getTrustLevelStyle = (level) => {
    switch (level) {
      case 'high': return 'bg-green-100 text-green-700 border-green-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const typeInfo = getEventTypeInfo(event.event_type);
  const dateDisplay = formatDate(event.date);
  const endDateDisplay = event.end_date && event.end_date !== event.date ? formatDate(event.end_date) : null;
  const locationDisplay = [event.city, event.state].filter(Boolean).join(', ') || event.location;
  
  // Use confidence_score from new Event Scout API, fallback to match_score for backward compat
  const confidenceScore = event.confidence_score ?? event.match_score;
  const trustLevel = event.trust_level;
  const sourceName = event.source_name || event.source;
  const validationWarnings = event.validation_warnings || [];
  const distanceDisplay = event.distance_miles ?? distance;

  return (
    <div 
      className={`bg-white border-2 rounded-2xl overflow-hidden transition-all duration-200 ${
        isSelected 
          ? 'border-[#1E3A5F] ring-2 ring-[#1E3A5F]/30 shadow-lg' 
          : validationWarnings.length > 0
            ? 'border-yellow-300 hover:border-yellow-400 hover:shadow-md'
            : 'border-[#E5E7EB] hover:border-[#1E3A5F] hover:shadow-md'
      }`}
      data-testid={`preview-event-card-${event.id}`}
    >
      {/* Card Header with event type visual */}
      <div className="relative h-32 bg-gradient-to-br from-[#1E3A5F]/10 to-[#1E3A5F]/5 flex items-center justify-center">
        <div className="text-center">
          <span className="text-4xl mb-1 block">{typeInfo.icon}</span>
          <span className="text-xs text-[#4A4A4A]">{event.event_type?.replace('_', ' ')}</span>
        </div>
        
        {/* Confidence Score Badge - Top Left */}
        {confidenceScore !== undefined && confidenceScore !== null && (
          <div 
            className={`absolute top-3 left-3 px-3 py-1.5 rounded-xl border-2 font-bold text-sm flex items-center gap-1 ${getConfidenceColor(confidenceScore)}`}
            data-testid={`confidence-score-${event.id}`}
            title={`Confidence: ${confidenceScore}%${trustLevel ? ` (${trustLevel} trust)` : ''}`}
          >
            {confidenceScore >= 80 ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : confidenceScore >= 60 ? (
              <Star className="w-4 h-4" />
            ) : (
              <AlertCircle className="w-4 h-4" />
            )}
            {confidenceScore}%
          </div>
        )}
        
        {/* Checkbox in top-right corner */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleSelect();
          }}
          className={`absolute top-3 right-3 w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all ${
            isSelected 
              ? 'bg-[#1E3A5F] border-[#1E3A5F] text-white' 
              : 'bg-white border-[#D1D5DB] hover:border-[#1E3A5F] text-transparent hover:text-[#1E3A5F]/50'
          }`}
          data-testid={`select-event-${event.id}`}
          aria-label={isSelected ? 'Deselect event' : 'Select event'}
        >
          <Check className="w-5 h-5" />
        </button>

        {/* Event type badge */}
        <span className={`absolute bottom-3 left-3 px-3 py-1 text-xs font-bold rounded-full border ${typeInfo.bg}`}>
          {typeInfo.icon} {typeInfo.label}
        </span>

        {/* Trust Level Badge - Bottom Right */}
        {trustLevel && (
          <span className={`absolute bottom-3 right-3 px-2 py-1 text-xs font-medium rounded-full border ${getTrustLevelStyle(trustLevel)}`}>
            {trustLevel.charAt(0).toUpperCase() + trustLevel.slice(1)} Trust
          </span>
        )}
      </div>

      {/* Validation Warnings Banner */}
      {validationWarnings.length > 0 && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-yellow-700">
            <span className="font-medium">Review needed:</span>
            <ul className="mt-1 space-y-0.5">
              {validationWarnings.slice(0, 2).map((warning, idx) => (
                <li key={idx}>• {warning}</li>
              ))}
              {validationWarnings.length > 2 && (
                <li className="text-yellow-600">+{validationWarnings.length - 2} more warnings</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Card Body */}
      <div className="p-5">
        <h3 className="font-bold font-['Outfit'] text-[#1E3A5F] text-lg mb-3 line-clamp-2">
          {event.title}
        </h3>

        <div className="space-y-2 text-sm text-[#4A4A4A]">
          {/* Location */}
          {locationDisplay && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#FE5F55] flex-shrink-0" />
              <span className="truncate">{locationDisplay}</span>
            </div>
          )}

          {/* Date */}
          {dateDisplay && (
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-[#1E3A5F] flex-shrink-0" />
              <span>
                {dateDisplay}
                {endDateDisplay && ` - ${endDateDisplay}`}
              </span>
            </div>
          )}

          {/* Cost */}
          {event.booth_cost && (
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 text-[#22C55E] flex-shrink-0 font-bold">$</span>
              <span className="font-semibold text-[#1E3A5F]">${event.booth_cost}</span>
            </div>
          )}

          {/* Distance */}
          {distanceDisplay && (
            <div className="flex items-center gap-2">
              <Compass className="w-4 h-4 text-[#6366F1] flex-shrink-0" />
              <span>{Math.round(distanceDisplay)} miles away</span>
            </div>
          )}

          {/* Event size */}
          {event.event_size && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[#71717A]">Size:</span>
              <span className="capitalize">{event.event_size}</span>
            </div>
          )}

          {/* Indoor/Outdoor */}
          {event.indoor_outdoor && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[#71717A]">Venue:</span>
              <span className="capitalize">{event.indoor_outdoor}</span>
            </div>
          )}
        </div>

        {/* Source info */}
        {sourceName && (
          <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
            <p className="text-xs text-[#71717A]">
              Source: <span className="font-medium">{sourceName}</span>
            </p>
          </div>
        )}

        {/* Website link - only show if URL exists and is valid */}
        {event.website_url && (
          <a
            href={event.website_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-sm text-[#1E3A5F] hover:text-[#1E3A5F] hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-3 h-3" />
            Visit Website
          </a>
        )}

        {/* No URL warning */}
        {!event.website_url && (
          <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
            <p className="text-xs text-yellow-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              No verified website URL
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIDiscover;
